import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, Loader2, Mic, MicOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { TipoAnaliseIa, VendaAnalise } from '@/lib/vendaAssistant';

type PictureInPictureApi = {
  requestWindow: (options: { width: number; height: number }) => Promise<Window>;
};

interface Props {
  cliente: string;
  analises: VendaAnalise[];
  isListening: boolean;
  isProcessing: boolean;
  secondsRemaining: number;
  lastError: string | null;
  onToggleListening: () => void;
}

const domains: { type: TipoAnaliseIa; label: string }[] = [
  { type: 'rapport', label: 'Rapport & Condução' },
  { type: 'tecnicas', label: 'Técnicas & Objeções' },
  { type: 'cliente', label: 'Cliente & Decisores' },
  { type: 'proposta', label: 'Proposta & Negociação' },
];

export function FloatingSalesCockpit(props: Props) {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const windowRef = useRef<Window | null>(null);
  const api = (window as Window & { documentPictureInPicture?: PictureInPictureApi }).documentPictureInPicture;

  useEffect(() => () => { windowRef.current?.close(); }, []);

  const openWindow = async () => {
    if (windowRef.current && !windowRef.current.closed) {
      windowRef.current.focus();
      return;
    }
    if (!api) {
      toast({ title: 'Janela flutuante indisponível', description: 'Abra o cockpit no Chrome ou Edge atualizado, em um computador.', variant: 'destructive' });
      return;
    }
    try {
      const floating = await api.requestWindow({ width: 360, height: 760 });
      floating.document.title = `Cockpit · ${props.cliente}`;
      document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
        const clone = node.cloneNode(true) as HTMLElement;
        if (node instanceof HTMLLinkElement && clone instanceof HTMLLinkElement) clone.href = node.href;
        floating.document.head.appendChild(clone);
      });
      floating.document.documentElement.className = document.documentElement.className;
      floating.document.body.className = 'floating-cockpit-body';
      windowRef.current = floating;
      setPipWindow(floating);
      // Placement is best-effort: the browser retains control over its own floating window.
      try { floating.moveTo(0, 80); } catch { /* Browser-managed placement. */ }
      floating.addEventListener('pagehide', () => {
        windowRef.current = null;
        setPipWindow(null);
      }, { once: true });
    } catch {
      toast({ title: 'Não foi possível abrir a janela', description: 'Tente novamente pelo botão Janela flutuante.', variant: 'destructive' });
    }
  };

  const speed = props.analises.find((item) => item.tipo === 'velocimetro');
  const score = Math.min(100, Math.max(0, speed?.score ?? 0));
  return <>
    <Button variant="outline" size="sm" className="shrink-0 gap-2" onClick={openWindow}>
      <ExternalLink className="h-4 w-4" />{pipWindow ? 'Mostrar janela flutuante' : 'Janela flutuante'}
    </Button>
    {pipWindow && createPortal(
      <div className="floating-cockpit">
        <header className="flex items-center gap-2 border-b border-border/60 px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">{props.cliente}</span>
          {props.isProcessing && <Loader2 aria-label="Analisando conversa" className="h-4 w-4 animate-spin text-primary" />}
          <span className="text-xs tabular-nums text-muted-foreground">{props.isListening ? `${Math.floor(props.secondsRemaining / 60)}:${String(props.secondsRemaining % 60).padStart(2, '0')}` : ''}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={props.isListening ? 'Pausar escuta' : 'Ativar microfone'} title={props.isListening ? 'Pausar escuta' : 'Ativar microfone'} onClick={props.onToggleListening}>
            {props.isListening ? <Mic className="h-4 w-4 text-primary" /> : <MicOff className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Fechar janela flutuante" title="Fechar janela flutuante" onClick={() => pipWindow.close()}><X className="h-4 w-4" /></Button>
        </header>
        <div className="space-y-2 p-2" aria-live="polite">
          <section className="floating-cockpit-tip">
            <div className="flex items-center gap-3">
              <div className="relative h-16 w-24 shrink-0" role="img" aria-label={`Velocímetro: ${speed ? score : 'sem nota'} de 100`}>
                <svg viewBox="0 0 100 60" className="h-full w-full" aria-hidden="true">
                  <path d="M 10 48 A 40 40 0 0 1 90 48" fill="none" stroke="hsl(var(--muted))" strokeWidth="7" strokeLinecap="round" />
                  <path d="M 10 48 A 40 40 0 0 1 90 48" fill="none" stroke="hsl(var(--primary))" strokeWidth="7" strokeLinecap="round" pathLength="100" strokeDasharray={`${score} 100`} />
                  <text x="50" y="48" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="22" fontWeight="700">{speed ? score : '—'}</text>
                </svg>
              </div>
              <h2 className="text-xs font-semibold text-primary">Velocímetro da Venda</h2>
            </div>
            <p className="break-words text-sm font-medium leading-snug">{speed?.sugestao_fala || 'Aguardando dica principal…'}</p>
          </section>
          {domains.map(({ type, label }) => {
            const analysis = props.analises.find((item) => item.tipo === type);
            return <section key={type} className="floating-cockpit-tip">
              <h2 className="mb-1 text-xs font-semibold text-primary">{label}</h2>
              <p title={analysis?.sugestao_fala || undefined} className="line-clamp-4 whitespace-pre-line break-words text-sm font-medium leading-snug">{analysis?.sugestao_fala || 'Aguardando análise…'}</p>
            </section>;
          })}
          {props.lastError && <p role="alert" className="break-words px-1 text-xs text-destructive">{props.lastError}</p>}
        </div>
      </div>, pipWindow.document.body
    )}
  </>;
}